import { getClockState } from '@/store/useTimeclockStore';
import {
  FacilityFlowCoreModulesPlanningEntitiesTimeClockDayStatus as TimeClockDayStatusEnum,
  FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType as TimeClockEventTypeEnum,
  type FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto as TimeClockDay,
  type FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto as TimeClockEvent,
} from '@/api/generated/models';
import { formatUtcToLocalTime } from '@/utils/dateUtils';

export type TFn = (key: string, options?: Record<string, unknown>) => string;

/** Per event of one day in chronological order: its work location and old vehicle text, or null. */
export type DescribeChoices = (orderedDayEvents: TimeClockEvent[]) => (string | null)[];

/** "28.09.2026" from "2026-09-28". */
export function formatIsoDay(date?: string) {
  if (!date) return '–';
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) return date;
  return `${day}.${month}.${year}`;
}

export const eventBadgeConfig: Record<number, { labelKey: string; shortKey: string; color: string; icon: string }> = {
  [TimeClockEventTypeEnum.NUMBER_1]: {
    labelKey: 'events.clockIn',
    shortKey: 'events.clockInShort',
    color: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    icon: '→',
  },
  [TimeClockEventTypeEnum.NUMBER_2]: {
    labelKey: 'events.clockOut',
    shortKey: 'events.clockOutShort',
    color: 'bg-red-50 text-red-700 border border-red-200',
    icon: '←',
  },
  [TimeClockEventTypeEnum.NUMBER_3]: {
    labelKey: 'events.breakStart',
    shortKey: 'events.breakStartShort',
    color: 'bg-amber-50 text-amber-700 border border-amber-200',
    icon: '⏸',
  },
  [TimeClockEventTypeEnum.NUMBER_4]: {
    labelKey: 'events.breakEnd',
    shortKey: 'events.breakEndShort',
    color: 'bg-sky-50 text-sky-700 border border-sky-200',
    icon: '▶',
  },
};

export function formatLastEvent(
  events: { eventType?: number; timestamp?: string; effectiveTimestamp?: string }[],
  t: TFn,
) {
  if (!events.length) return '–';
  const last = events[events.length - 1];
  if (!last.timestamp) return '–';
  const time = formatUtcToLocalTime(last.effectiveTimestamp ?? last.timestamp);
  const config = last.eventType ? eventBadgeConfig[last.eventType] : null;
  const label = config ? t(config.labelKey) : t('events.clockIn');
  return t('events.atTime', { label, time });
}

export function canConfirm(entry: TimeClockDay) {
  if (entry.status !== TimeClockDayStatusEnum.NUMBER_1) return false;
  return getClockState(entry) === 'out';
}
